// Metadata for rendering older-quiz results in the Admin dashboard + Practitioner Worksheet.
// Mirrors the question/scoring definitions in MetabolicQuizPage.jsx and LifestyleAssessmentPage.jsx.
// Keep in sync if those quizzes change.

// ---------- Metabolic Rhythm Quiz ----------
export const metabolicQuestions = [
  { q: 'After eating a protein-rich meal (meat, eggs, fish), I usually feel:', A: 'Satisfied and steady for hours', B: 'Heavy or sluggish', C: 'Fine, but not noticeably different' },
  { q: 'If I skip a meal, I tend to:', A: 'Get irritable or shaky quickly', B: 'Feel okay for quite a while', C: 'Notice hunger but function normally' },
  { q: 'After eating sweets or refined carbohydrates:', A: 'I feel good briefly, then crash', B: 'I feel mostly fine', C: 'It depends on stress levels' },
  { q: 'My daily energy feels best when meals are:', A: 'Substantial, including protein and fats', B: 'Lighter and easier to digest', C: 'Balanced between protein, fats, and carbohydrates' },
  { q: 'After intense exercise, I crave:', A: 'Protein and savory foods', B: 'Fruit or something sweet', C: 'A mix of both' },
  { q: 'My body tends to:', A: 'Build muscle relatively easily', B: 'Stay lean without much effort', C: 'Change depending on lifestyle' },
  { q: 'When stressed, I tend to:', A: 'Crave heavier comfort-style foods', B: 'Lose appetite or prefer lighter foods', C: 'Either overeat or undereat depending on the situation' },
  { q: 'I sleep best when:', A: 'I eat a solid dinner with protein and fats', B: 'I eat lighter in the evening', C: 'I avoid extremes and eat moderately' },
  { q: 'My mental focus feels most stable when:', A: "I've had a protein-forward meal", B: "I've had lighter fare", C: "I've balanced both" },
  { q: 'My appetite is generally:', A: 'Strong and reliable', B: 'Variable and sometimes subtle', C: 'Moderate and predictable' },
  { q: 'My taste preference leans toward:', A: 'Savory and salty', B: 'Mild and lightly seasoned', C: 'Balanced between both' },
  { q: 'My digestion feels best when meals are:', A: 'Dense and satisfying', B: 'Lighter and simpler', C: 'Moderate in size and composition' },
  { q: 'If I eat mostly carbohydrates at a meal:', A: 'I feel hungry again fairly quickly', B: 'I feel energized and light', C: 'It depends on portion size and context' },
  { q: 'My relationship to food is:', A: 'I genuinely enjoy food and look forward to meals', B: 'I eat more for function than pleasure', C: "I enjoy food but don't obsess" },
  { q: 'My immune resilience tends to be:', A: 'Strong and steady', B: 'Sensitive or easily thrown off', C: 'Moderate' },
  { q: 'My response to caffeine is:', A: "I don't tolerate much without feeling wired or anxious", B: 'I tolerate it fairly well', C: 'It depends on sleep and stress' },
];

export const metabolicRhythmLabels = {
  grounded: 'Grounded Burner',
  light: 'Light & Quick',
  balanced: 'Steady Balanced',
};

// A→grounded, B→light, C→balanced
export const metabolicLetterMeaning = {
  A: 'Grounded Burner (protein + fats)',
  B: 'Light & Quick (lighter carbs)',
  C: 'Steady Balanced (mixed)',
};

// ---------- Lifestyle / Vitality Potential Tool ----------
export const lifestyleAnswerLabels = { 0: 'Never', 1: 'Rarely', 3: 'Sometimes', 5: 'Often', 8: 'Consistently' };
const reverseMap = { 0: 8, 1: 5, 3: 3, 5: 1, 8: 0 };
export const scoreLifestyleValue = (value, isReverse) => (isReverse ? (reverseMap[value] ?? value) : value);

export const getLifestyleDomainStatus = (score, maxScore) => {
  const pct = (score / maxScore) * 100;
  if (pct <= 25) return 'Resilient';
  if (pct <= 50) return 'Compensating';
  return 'Dysregulated';
};

export const lifestyleDomains = [
  { id: 'food-quality', title: 'Food Quality & Nutrient Density', icon: '🥗', questions: [
    { id: 'fq1', text: 'How often do you consume non-organic produce?' },
    { id: 'fq2', text: 'How often do you consume conventionally raised meat or poultry?' },
    { id: 'fq3', text: 'How often do you consume ultra-processed foods (boxed, packaged, frozen meals)?' },
    { id: 'fq4', text: 'How often do you consume refined seed oils (canola, soybean, vegetable oil)?' },
    { id: 'fq5', text: 'How often do you consume artificial sweeteners?' },
    { id: 'fq6', text: 'How often do you consume refined white sugar or high-fructose products?' },
    { id: 'fq7', text: 'How often do you drink unfiltered tap water?' },
    { id: 'fq8', text: 'How often do you eat fewer than 2–3 servings of vegetables daily?' },
  ] },
  { id: 'meal-rhythm', title: 'Meal Rhythm & Blood Sugar Stability', icon: '⏰', questions: [
    { id: 'mr1', text: 'How often do you skip meals?' },
    { id: 'mr2', text: 'How often do you go more than 4–5 hours without eating?' },
    { id: 'mr3', text: 'How often do you eat carbohydrate-heavy meals without protein or fat?' },
    { id: 'mr4', text: 'How often do you crave sweets within two hours of eating?' },
    { id: 'mr5', text: 'How often do you rely on caffeine to maintain energy?' },
    { id: 'mr6', text: 'How often do you feel shaky, irritable, or foggy if you miss a meal?' },
    { id: 'mr7', text: 'How often is your largest meal eaten late in the evening?' },
    { id: 'mr8', text: 'How often do you snack due to stress rather than hunger?' },
  ] },
  { id: 'digestive', title: 'Digestive Function', icon: '🌿', questions: [
    { id: 'df1', text: 'How often do you experience bloating?' },
    { id: 'df2', text: 'How often do you experience constipation?' },
    { id: 'df3', text: 'How often do you experience loose stools or urgency?' },
    { id: 'df4', text: 'How often do you experience gas or belching after meals?' },
    { id: 'df5', text: 'How often do you experience abdominal pain or cramping?' },
    { id: 'df6', text: 'How often do you experience heartburn or indigestion?' },
    { id: 'df7', text: 'How often do you notice undigested food in stool?' },
    { id: 'df8', text: 'How often do you experience headaches or fatigue after eating?' },
  ] },
  { id: 'detox', title: 'Detoxification & Toxic Load', subtitle: 'Internal Expression', icon: '💧', questions: [
    { id: 'dt1', text: 'How often do you experience unexplained fatigue or sluggishness?' },
    { id: 'dt2', text: 'How often do you experience frequent headaches?' },
    { id: 'dt3', text: 'How often do you experience skin reactions (rashes, itching, acne)?' },
    { id: 'dt4', text: 'How often do you have dark circles or puffiness under the eyes?' },
    { id: 'dt5', text: 'How often are you sensitive to smells, perfumes, or chemical fumes?' },
    { id: 'dt6', text: 'How often do you feel mentally foggy or have poor concentration?' },
    { id: 'dt7', text: 'How often do you retain cellulite or water despite exercise?' },
    { id: 'dt8', text: 'How often do you feel worse in certain environments (offices, stores, hotels)?' },
  ] },
  { id: 'environmental', title: 'Environmental & Chemical Exposure', subtitle: 'External Load', icon: '🏠', questions: [
    { id: 'ev1', text: 'How often do you use fragranced laundry detergents or fabric softeners?' },
    { id: 'ev2', text: 'How often do you use scented candles, plug-ins, or air fresheners?' },
    { id: 'ev3', text: 'How often do you use conventional cleaning products?' },
    { id: 'ev4', text: 'How often do you store food in plastic containers?' },
    { id: 'ev5', text: 'How often do you use non-stick cookware?' },
    { id: 'ev6', text: 'How often do you use conventional cosmetics or personal care products?' },
    { id: 'ev7', text: 'How often are you exposed to traffic fumes or industrial pollution?' },
    { id: 'ev8', text: 'How often are you exposed to mold or damp environments?' },
  ] },
  { id: 'circadian', title: 'Circadian Rhythm & Light Hygiene', icon: '🌙', questions: [
    { id: 'cr1', text: 'How often do you go to bed after 10:30 PM?' },
    { id: 'cr2', text: 'How often do you wake between 1:00–4:00 AM?' },
    { id: 'cr3', text: 'How often do you wake feeling unrefreshed?' },
    { id: 'cr4', text: 'How often do you view screens within 30 minutes of sleep?' },
    { id: 'cr5', text: 'How often do you check your phone immediately upon waking?' },
    { id: 'cr6', text: 'How often do you get morning sunlight within 30 minutes of waking?', reverse: true },
    { id: 'cr7', text: 'How often do you feel a significant afternoon energy crash?' },
    { id: 'cr8', text: 'How often do you sleep in a fully dark room?', reverse: true },
  ] },
  { id: 'stress', title: 'Stress & Emotional Load', icon: '💭', questions: [
    { id: 'st1', text: 'How often do you feel financially stressed?' },
    { id: 'st2', text: 'How often do you feel relationship stress?' },
    { id: 'st3', text: 'How often do you feel anxious without clear cause?' },
    { id: 'st4', text: 'How often do you feel overwhelmed by daily demands?' },
    { id: 'st5', text: 'How often do you consume news or social media that elevates stress?' },
    { id: 'st6', text: 'How often do you feel reactive rather than responsive?' },
    { id: 'st7', text: 'How often do you feel isolated or unsupported?' },
    { id: 'st8', text: 'How often do you feel joy or lightness during the week?', reverse: true },
  ] },
  { id: 'nervous-system', title: 'Nervous System Regulation & Capacity', icon: '🧘', questions: [
    { id: 'ns1', text: 'How often do you feel "on edge" even when nothing is wrong?' },
    { id: 'ns2', text: 'How often do you clench your jaw, shoulders, or abdomen?' },
    { id: 'ns3', text: 'How often do you breathe through your mouth at rest?' },
    { id: 'ns4', text: 'How often do you feel chronically rushed?' },
    { id: 'ns5', text: 'How often do you struggle to relax without distraction?' },
    { id: 'ns6', text: 'How often do you feel safe and grounded in your body?', reverse: true },
    { id: 'ns7', text: 'How often do you practice intentional stillness or mindfulness?', reverse: true },
    { id: 'ns8', text: 'How often do you rely on food, alcohol, scrolling, or stimulation to calm down?' },
  ] },
];

// Build normalized section scores [{name, score, max, band}] for the worksheet, per quiz type.
export const buildSectionScores = (result) => {
  const qt = result.quiz_type;
  if (qt === 'wellness_pattern_assessment') {
    const scores = (result.answers && result.answers.scores) || {};
    return Object.entries(scores).map(([name, info]) => ({ name, score: info.score, max: 20, band: info.band }))
      .sort((a, b) => b.score - a.score);
  }
  if (qt === 'lifestyle_assessment' || qt === 'lifestyle-assessment') {
    const answers = result.answers || {};
    return lifestyleDomains.map((d) => {
      const max = d.questions.length * 8;
      let score = 0;
      d.questions.forEach((q) => {
        if (answers[q.id] !== undefined) score += scoreLifestyleValue(answers[q.id], q.reverse);
      });
      return { name: d.title, score, max, band: getLifestyleDomainStatus(score, max) };
    }).sort((a, b) => b.score - a.score);
  }
  if (qt === 'metabolic_rhythm') {
    const answers = result.answers || {};
    const counts = { A: 0, B: 0, C: 0 };
    Object.values(answers).forEach((v) => { if (counts[v] !== undefined) counts[v] += 1; });
    const total = counts.A + counts.B + counts.C || 1;
    return [
      { name: 'Grounded Burner (A)', score: counts.A, max: total, band: `${counts.A} of ${total}` },
      { name: 'Light & Quick (B)', score: counts.B, max: total, band: `${counts.B} of ${total}` },
      { name: 'Steady Balanced (C)', score: counts.C, max: total, band: `${counts.C} of ${total}` },
    ];
  }
  return [];
};

export const quizHighLoadBand = {
  wellness_pattern_assessment: 'High Load',
  lifestyle_assessment: 'Dysregulated',
  'lifestyle-assessment': 'Dysregulated',
};
